import { useState, useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import {
    DataGridPro,
    GridCellEditStopReasons,
    GridColDef,
    GridRenderCellParams,
    GridDataSource,
    GridToolbarProps,
} from '@mui/x-data-grid-pro'
import dayjs from 'dayjs'
import { MPUpload, MPSession } from '@types'
import { formatDurationHms } from '@utils'
import { Link } from 'wouter'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline'
import { LoadingButton } from '@mui/lab'
import type { EnqueueSnackbar } from 'notistack'
import API from '@enums/api'
import { UseDataSource } from '@hooks/useDataSource'
import CustomToolbar from '@components/CustomToolbar'
import { ToolbarPropsOverrides } from '@mui/x-data-grid'
import DurationEditCell from './DurationEditCell'

export type SessionsData = {
    id: MPSession['id']
    title: MPSession['title']
    userId: MPSession['userId']
    createdAt: MPSession['createdAt']
    uid?: MPUpload['id']
    uploadedAt?: MPUpload['createdAt']
    status?: MPUpload['status']
    mimeType?: MPUpload['mimeType']
    duration?: MPUpload['duration']
    userName: string
    org?: {
        orgId: string
        orgName: string
    }
    orderId?: string
}

interface Props {
    showUser?: boolean
    enqueueSnackbar?: EnqueueSnackbar
    dataSource: GridDataSource
    showExport?: true
    exporting: UseDataSource['exporting']
    exportAllRows: UseDataSource['exportAllRows']
    setFilterModel: UseDataSource['setFilterModel']
}

export default function SessionsDataGrid({
    showUser = false,
    enqueueSnackbar,
    dataSource,
    showExport,
    exporting,
    exportAllRows,
    setFilterModel,
}: Props) {
    const [buttonStates, setButtonStates] = useState<Record<string, boolean>>(
        {}
    )

    const changeButtonState = (id: string, state: boolean) =>
        setButtonStates((prev) => ({ ...prev, [id]: state }))

    const handleRetryClick = async (sessionId: string, uploadId?: string) => {
        if (!uploadId) return
        changeButtonState(sessionId, true)
        const res = await API.mp.uploads.convertAudio(uploadId)
        changeButtonState(sessionId, false)
        if (!res.ok && enqueueSnackbar) {
            enqueueSnackbar(`${res.status}: ${res.message}`, {
                variant: 'error',
            })
        } else if (enqueueSnackbar) {
            enqueueSnackbar('Audio converted', { variant: 'success' })
        }
    }

    const handleDownloadClick = async (sessionId: string, uploadId: string) => {
        changeButtonState(sessionId, true)

        // Upload-keyed, not order-keyed: works for any session's media,
        // not just sessions with an (order-type-restricted) order attached.
        const res = await API.mp.staffUploads.getPresignedUrl(uploadId, 'audio')

        changeButtonState(sessionId, false)
        if (!res.ok) {
            console.error(res.error)
            if (enqueueSnackbar) {
                enqueueSnackbar(`${res.status}: ${res.message}`, {
                    variant: 'error',
                })
            }
            return
        }

        const { signedUrl } = res.value
        window.open(signedUrl, '_blank')
    }

    /**
     * The warning and the confirmation live in DurationEditCell, which is
     * where the person can still see what they typed next to what is there
     * now. By the time this runs they have agreed to it.
     */
    const handleDurationUpdate = async (
        row: SessionsData,
        newDurationMs: number
    ): Promise<SessionsData> => {
        if (!row.uid) return row

        const res = await API.mp.staffUploads.setDuration(
            row.uid,
            newDurationMs
        )

        if (!res.ok) {
            if (enqueueSnackbar) {
                enqueueSnackbar(`${res.status}: ${res.message}`, {
                    variant: 'error',
                })
            }
            throw new Error('failed to update duration')
        }

        if (enqueueSnackbar) {
            enqueueSnackbar('Upload duration updated', {
                variant: 'success',
            })
        }
        return { ...row, duration: newDurationMs }
    }

    const columns = useMemo<GridColDef[]>(() => {
        const cols: GridColDef[] = [
            {
                field: 'title',
                headerName: 'Title',
                flex: 1.5,
                type: 'string',
                renderCell: (params: GridRenderCellParams<SessionsData>) => (
                    <Stack direction="column">
                        <Typography variant="body1">
                            {params.row.title}
                        </Typography>
                        <Typography variant="overline" color="gray">
                            {params.row.id}
                        </Typography>
                    </Stack>
                ),
            },
            {
                field: 'createdAt',
                headerName: 'Session Date',
                flex: 1,
                type: 'dateTime',
                valueFormatter: (value) =>
                    value
                        ? dayjs(value as string).format('YYYY/MM/DD HH:mm:ss')
                        : '',
            },
            {
                field: 'uploadedAt',
                headerName: 'Upload Date',
                flex: 1,
                type: 'dateTime',
                valueFormatter: (value) =>
                    value
                        ? dayjs(value as string).format('YYYY/MM/DD HH:mm:ss')
                        : '',
            },
            {
                field: 'duration',
                headerName: 'Upload Duration',
                flex: 1,
                type: 'number',
                valueFormatter: formatDurationHms,
                editable: true,
                renderEditCell: (params) => <DurationEditCell {...params} />,
            },
            {
                field: 'status',
                headerName: 'Upload Status',
                flex: 1,
                type: 'string',
            },
            {
                field: 'mimeType',
                headerName: 'Upload Format',
                flex: 1,
                type: 'string',
            },
            {
                field: 'type',
                headerName: 'Upload Type',
                flex: 1,
                type: 'string',
            },
            {
                field: 'download',
                headerName: 'Download',
                flex: 0.5,
                type: 'actions',
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<SessionsData>) => (
                    <LoadingButton
                        loading={buttonStates[params.row.id]}
                        disabled={!params.row.uid}
                        onClick={() => {
                            handleDownloadClick(params.row.id, params.row.uid!)
                        }}
                    >
                        <DownloadForOfflineIcon />
                    </LoadingButton>
                ),
            },
            {
                field: 'retry',
                headerName: 'Retry',
                flex: 0.5,
                type: 'actions',
                sortable: false,
                renderCell: (params: GridRenderCellParams<SessionsData>) => (
                    <LoadingButton
                        loading={buttonStates[params.row.id]}
                        disabled={!params.row.uid}
                        onClick={() =>
                            handleRetryClick(params.row.id, params.row.uid)
                        }
                    >
                        <RestartAltIcon />
                    </LoadingButton>
                ),
            },
        ]
        if (showUser) {
            cols.splice(
                1,
                0,
                {
                    field: 'userName',
                    headerName: 'User',
                    filterable: false,
                    sortable: false,
                    flex: 1,
                    type: 'string',
                    renderCell: (
                        params: GridRenderCellParams<SessionsData>
                    ) => (
                        <Link href={`/user-deepdive?q=${params.row.userId}`}>
                            {params.row.userName}
                        </Link>
                    ),
                },
                {
                    field: 'org',
                    headerName: 'Organization',
                    filterable: false,
                    sortable: false,
                    flex: 1,
                    type: 'string',
                    valueGetter: (_, row) => {
                        return row.org ? row.org.orgName : 'Individual'
                    },
                    renderCell: (
                        params: GridRenderCellParams<SessionsData>
                    ) => {
                        if (!params.row.org) {
                            return (
                                <Typography
                                    variant="body1"
                                    height={'100%'}
                                    alignItems={'center'}
                                >
                                    Individual
                                </Typography>
                            )
                        }
                        return (
                            <Link
                                href={`/organizations/${params.row.org.orgId}`}
                            >
                                {params.row.org.orgName}
                            </Link>
                        )
                    },
                }
            )
        }
        return cols
    }, [buttonStates, showUser])

    const toolbarProps: GridToolbarProps & ToolbarPropsOverrides = showExport
        ? {
              exporting,
              exportAllRows,
              exportAs: 'csv',
              exportFilename: 'sessions',
          }
        : {}

    return (
        <Box width="100%">
            <Box sx={{ height: 600, width: '100%' }}>
                <DataGridPro
                    columns={columns}
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    onFilterModelChange={(model) => setFilterModel(model)}
                    dataSource={dataSource}
                    isCellEditable={(params) =>
                        params.field !== 'duration' ||
                        Boolean((params.row as SessionsData).uid)
                    }
                    onCellEditStop={(params, event) => {
                        // The duration editor is a dialog, which portals out
                        // of the grid, so opening it reads as the cell losing
                        // focus and would close the editor underneath it.
                        if (
                            params.reason ===
                            GridCellEditStopReasons.cellFocusOut
                        ) {
                            event.defaultMuiPrevented = true
                        }
                    }}
                    processRowUpdate={(newRow, oldRow) =>
                        newRow.duration !== oldRow.duration
                            ? handleDurationUpdate(oldRow, newRow.duration)
                            : newRow
                    }
                    onProcessRowUpdateError={(error) => {
                        console.error(error)
                    }}
                    pageSizeOptions={[10, 20, 50, 100]}
                    disableRowSelectionOnClick
                    disableVirtualization
                    showToolbar
                    slots={{
                        toolbar: showExport ? CustomToolbar : undefined,
                    }}
                    slotProps={{
                        toolbar: {
                            quickFilterProps: {
                                quickFilterParser: (searchInput) =>
                                    searchInput
                                        .split(',')
                                        .map((value) => value.trim()),
                                quickFilterFormatter: (quickFilterValues) =>
                                    quickFilterValues.join(', '),
                                debounceMs: 250,
                            },
                            ...toolbarProps,
                        },
                    }}
                    getRowId={(row) => row.id}
                />
            </Box>
        </Box>
    )
}
